import React from 'react';
import { StaffDeal } from '@/hooks/useStaffDeals';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, Download, RefreshCw, UserPlus, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useLanguage } from '@/contexts/LanguageContext';
import { StaffDealRowActions } from './StaffDealRowActions';

interface StaffDealsTableProps {
  deals: StaffDeal[];
  view: 'todo' | 'triage' | 'all';
  onTakeDeal?: (dealId: string) => void;
  onViewDeal?: (dealId: string) => void;
  onGenerateNDA?: (dealId: string) => void;
  onGenerateCIM?: (dealId: string) => void;
  onAdvanceStage?: (dealId: string) => void;
}

const T = {
  en: {
    priority: { high: 'High', normal: 'Normal', low: 'Low' },
    status: {
      pending_triage: 'To sort',
      pending_review: 'To review',
      review_in_progress: 'In review',
      evaluation_in_progress: 'Evaluating',
      cim_ready_for_internal_review: 'CIM to review',
      awaiting_seller_approval: 'Awaiting seller',
      awaiting_final_payment: 'Awaiting payment',
      ready_to_publish: 'Ready to publish',
      needs_more_info: 'Awaiting info',
      published: 'Published',
    },
    nda: { signed: 'Signed', sent_to_buyer: 'Sent', declined: 'Declined', not_required: 'N/A' },
    headers: { title: 'Deal Title', role: 'Client Role', priority: 'Priority', status: 'Status', nda: 'NDA', submitted: 'Submitted', actions: 'Actions' },
    emptyTriage: 'No files to sort. All deals are assigned.',
    emptyDefault: 'No files to display.',
    takeDeal: 'Take over',
    viewDetail: 'View detail',
    generateNDA: 'Generate NDA',
    generateCIM: 'Generate CIM',
    advanceStage: 'Advance stage',
    ndaWarning: '* Deals with an NDA pending signature for over 48h are highlighted in red.',
  },
  fr: {
    priority: { high: 'Haute', normal: 'Normale', low: 'Basse' },
    status: {
      pending_triage: 'À trier',
      pending_review: 'À revoir',
      review_in_progress: 'En revue',
      evaluation_in_progress: 'En évaluation',
      cim_ready_for_internal_review: 'CIM à réviser',
      awaiting_seller_approval: 'Attente vendeur',
      awaiting_final_payment: 'Attente paiement',
      ready_to_publish: 'Prêt à publier',
      needs_more_info: 'Attente info',
      published: 'Publié',
    },
    nda: { signed: 'Signé', sent_to_buyer: 'Envoyé', declined: 'Refusé', not_required: 'N/A' },
    headers: { title: 'Titre du Deal', role: 'Rôle Client', priority: 'Priorité', status: 'Statut', nda: 'NDA', submitted: 'Soumission', actions: 'Actions' },
    emptyTriage: 'Aucun dossier à trier. Tous les deals sont assignés.',
    emptyDefault: 'Aucun dossier à afficher.',
    takeDeal: 'Prendre en charge',
    viewDetail: 'Voir détail',
    generateNDA: 'Générer NDA',
    generateCIM: 'Générer CIM',
    advanceStage: "Avancer l'étape",
    ndaWarning: '* Les deals avec un NDA en attente de signature depuis plus de 48h sont mis en évidence en rouge.',
  },
};

export const StaffDealsTable: React.FC<StaffDealsTableProps> = ({ deals, view, onTakeDeal, onViewDeal, onGenerateNDA, onGenerateCIM, onAdvanceStage }) => {
  const { language } = useLanguage();
  const t = T[language] || T.en;
  const locale = language === 'fr' ? fr : enUS;

  const getPriorityBadge = (priority: StaffDeal['priority']) => {
    const styles = {
      high: 'bg-red-500/20 text-red-400 border-red-500/30',
      normal: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      low: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    };
    return <Badge variant="outline" className={styles[priority]}>{t.priority[priority]}</Badge>;
  };

  const getStatusBadge = (status: StaffDeal['status']) => {
    const statusStyles: Record<string, string> = {
      pending_triage: 'bg-purple-500/20 text-purple-400',
      pending_review: 'bg-blue-500/20 text-blue-400',
      review_in_progress: 'bg-cyan-500/20 text-cyan-400',
      evaluation_in_progress: 'bg-indigo-500/20 text-indigo-400',
      cim_ready_for_internal_review: 'bg-emerald-500/20 text-emerald-400',
      awaiting_seller_approval: 'bg-amber-500/20 text-amber-400',
      awaiting_final_payment: 'bg-orange-500/20 text-orange-400',
      ready_to_publish: 'bg-green-500/20 text-green-400',
      needs_more_info: 'bg-red-500/20 text-red-400',
      published: 'bg-teal-500/20 text-teal-400',
    };
    const style = statusStyles[status] || 'bg-gray-500/20 text-gray-400';
    const label = (t.status as Record<string, string>)[status] || status;
    return <Badge variant="outline" className={style}>{label}</Badge>;
  };

  const getNdaBadge = (ndaStatus: StaffDeal['ndaStatus'], ndaSentAt: Date | null) => {
    const styles: Record<string, string> = {
      signed: 'bg-green-500/20 text-green-400',
      sent_to_buyer: 'bg-amber-500/20 text-amber-400',
      declined: 'bg-red-500/20 text-red-400',
      not_required: 'bg-gray-500/20 text-gray-400',
    };
    let isOverdue = false;
    if (ndaStatus === 'sent_to_buyer' && ndaSentAt) {
      isOverdue = (Date.now() - ndaSentAt.getTime()) / (1000 * 60 * 60) > 48;
    }
    return (
      <Badge variant="outline" className={`${styles[ndaStatus]} ${isOverdue ? 'ring-2 ring-red-500 animate-pulse' : ''}`}>
        {(t.nda as Record<string, string>)[ndaStatus]}{isOverdue && ' ⚠️'}
      </Badge>
    );
  };

  const getRoleBadge = (role: StaffDeal['role']) => {
  const styles: Record<string, string> = {
    Seller: 'bg-blue-500/20 text-blue-400 border-blue-500/40',
    Broker: 'bg-purple-500/20 text-purple-400 border-purple-500/40',
    BYOD: 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/50 font-semibold',
    'Buyer Elite': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40',
  };
  const style = styles[role] || 'bg-gray-500/20 text-gray-400 border-gray-500/40';
  return <Badge variant="outline" className={style}>{role}</Badge>;
};

  if (deals.length === 0) {
    return (
      <div className="bg-gray-800/50 rounded-xl p-8 text-center">
        <p className="text-gray-400">{view === 'triage' ? t.emptyTriage : t.emptyDefault}</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700 bg-gray-900/50">
              <th className="text-left p-4 text-xs uppercase tracking-wide text-gray-400 font-semibold">{t.headers.title}</th>
              <th className="text-left p-4 text-xs uppercase tracking-wide text-gray-400 font-semibold">{t.headers.role}</th>
              <th className="text-left p-4 text-xs uppercase tracking-wide text-gray-400 font-semibold">{t.headers.priority}</th>
              <th className="text-left p-4 text-xs uppercase tracking-wide text-gray-400 font-semibold">{t.headers.status}</th>
              {view !== 'triage' && (
                <th className="text-left p-4 text-xs uppercase tracking-wide text-gray-400 font-semibold">{t.headers.nda}</th>
              )}
              <th className="text-left p-4 text-xs uppercase tracking-wide text-gray-400 font-semibold">{t.headers.submitted}</th>
              <th className="text-left p-4 text-xs uppercase tracking-wide text-gray-400 font-semibold">{t.headers.actions}</th>
            </tr>
          </thead>
          <tbody>
            {deals.map((deal) => {
              let isNdaOverdue = false;
              if (deal.ndaStatus === 'sent_to_buyer' && deal.ndaSentAt) {
                isNdaOverdue = (Date.now() - deal.ndaSentAt.getTime()) / (1000 * 60 * 60) > 48;
              }
              return (
                <tr key={deal.id} className={`border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors ${isNdaOverdue ? 'bg-red-900/10' : ''}`}>
                  <td className="p-4">
                    <div>
                      <p className="font-medium text-white">{deal.title}</p>
                      <p className="text-xs text-gray-500">{deal.id}</p>
                    </div>
                  </td>
                  <td className="p-4">{getRoleBadge(deal.role)}</td>
                  <td className="p-4">{getPriorityBadge(deal.priority)}</td>
                  <td className="p-4">{getStatusBadge(deal.status)}</td>
                  {view !== 'triage' && (
                    <td className="p-4">{getNdaBadge(deal.ndaStatus, deal.ndaSentAt)}</td>
                  )}
                  <td className="p-4">
                    <span className="text-sm text-gray-400">
                      {formatDistanceToNow(deal.submittedAt, { addSuffix: true, locale })}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {view === 'triage' ? (
                        <Button size="sm" onClick={() => onTakeDeal?.(deal.id)} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                          <UserPlus className="w-4 h-4 mr-1" />
                          {t.takeDeal}
                        </Button>
                      ) : (
  <StaffDealRowActions deal={deal} onViewDeal={onViewDeal} onRefresh={() => {}} />
)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {view !== 'triage' && (
        <div className="p-3 bg-gray-900/30 border-t border-gray-700 text-xs text-gray-500">
          {t.ndaWarning}
        </div>
      )}
    </div>
  );
};
